import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { createCustomTheme, CustomTheme, defaultTheme, classicalThemes } from '../theme/theme';

interface ThemeContextType {
  theme: CustomTheme;
  setTheme: (theme: CustomTheme) => void;
  setMode: (mode: 'light' | 'dark') => void;
  setPrimaryColor: (color: string) => void;
  setSecondaryColor: (color: string) => void;
  setAccentColor: (color: string) => void;
  applyClassicalTheme: (themeName: keyof typeof classicalThemes) => void;
  resetTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<CustomTheme>(() => {
    const savedTheme = localStorage.getItem('ishaan-construction-theme');
    if (savedTheme) {
      try {
        return JSON.parse(savedTheme);
      } catch {
        return defaultTheme;
      }
    }
    return defaultTheme;
  });

  const setTheme = (newTheme: CustomTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('ishaan-construction-theme', JSON.stringify(newTheme));
  };

  const setMode = (mode: 'light' | 'dark') => {
    setTheme({ ...theme, mode });
  };

  const setPrimaryColor = (color: string) => {
    setTheme({ ...theme, primaryColor: color });
  };

  const setSecondaryColor = (color: string) => {
    setTheme({ ...theme, secondaryColor: color });
  };

  const setAccentColor = (color: string) => {
    setTheme({ ...theme, accentColor: color });
  };

  const applyClassicalTheme = (themeName: keyof typeof classicalThemes) => {
    setTheme(classicalThemes[themeName]);
  };

  const resetTheme = () => {
    setTheme(defaultTheme);
  };

  const muiTheme = createCustomTheme(theme);

  const contextValue: ThemeContextType = {
    theme,
    setTheme,
    setMode,
    setPrimaryColor,
    setSecondaryColor,
    setAccentColor,
    applyClassicalTheme,
    resetTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

